import { serverTimestamp } from 'firebase/firestore';

import {
  DEFAULT_AGENT_CONFIG,
  DEFAULT_LLM_MODEL,
  DEFAULT_LLM_PROVIDER,
  DEFAULT_NPC_MAX_TOKENS,
  DEFAULT_NPC_TEMPERATURE,
  DEFAULT_PERSONALITY_SPEC,
} from '../../../npc/config';
import { toPersonalitySpec } from '../../../lib/personalitySpec';
import {
  canonicalizeProviderModel,
  cloneFallbackProviders,
  normalizeFallbackProviders,
} from '../../../lib/llmConfig';
import type {
  AgentConfig,
  AgentToolName,
  NpcDefinition,
  NpcVisibility,
  PersonalitySpec,
  ToolPermission,
  UserNpcConfig,
} from '../../../types/npc';
import {
  DEFAULT_SPRITE_COLORS,
  type AvatarLayerSelection,
  type SpriteColors,
} from '../../../types/user';
import type { AvatarMode } from '../../../types/customAvatar';
import { normalizeMoodProfile, normalizeMoodState, type NpcMoodProfile } from '../../../types/mood';
import {
  DEFAULT_AVATAR_LAYER_SELECTION,
  normalizeAvatarLayerSelection,
} from '../../../ui/avatarGeneratorOptions';
import {
  toAvatarLayerSelection,
  toAvatarMode,
  toHexColor,
  toOptionalString,
  toTimestampDate,
} from './documentParsers';

export interface UserNpcUpsertInput {
  name: string;
  title: string;
  expertise: string;
  backstory: string;
  personality: string;
  greeting: string;
  avatarLayers: AvatarLayerSelection;
  spriteColors: SpriteColors;
  avatarMode?: AvatarMode;
  customAvatarId?: string | null;
  personalitySpec: PersonalitySpec | null;
  llmProvider: NpcDefinition['llmProvider'];
  llmModel: string;
  fallbackProviders?: NpcDefinition['fallbackProviders'];
  temperature: number;
  maxTokens: number;
  visibility: NpcVisibility;
  isActive: boolean;
  agentConfig?: AgentConfig | null;
  moodProfile: NpcMoodProfile;
}

const VALID_TOOL_PERMISSIONS: ToolPermission[] = [
  'read_memory',
  'write_memory',
  'web_access',
  'game_world',
  'task_management',
  'skill_execution',
];

const VALID_AGENT_TOOLS: AgentToolName[] = [
  'memory_store',
  'memory_recall',
  'task_update',
  'game_emote',
  'game_navigate',
  'interact_with',
  'web_search',
  'web_fetch',
  'delegate_research',
  'skill_execute',
];

const normalizePermissionList = (value: unknown): ToolPermission[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  const unique = new Set<ToolPermission>();
  value.forEach((entry) => {
    if (VALID_TOOL_PERMISSIONS.includes(entry as ToolPermission)) {
      unique.add(entry as ToolPermission);
    }
  });
  return [...unique];
};

const normalizeAgentToolList = (value: unknown): AgentToolName[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  const unique = new Set<AgentToolName>();
  value.forEach((entry) => {
    if (VALID_AGENT_TOOLS.includes(entry as AgentToolName)) {
      unique.add(entry as AgentToolName);
    }
  });
  return [...unique];
};

const ensureDelegateResearchTool = (
  tools: AgentToolName[],
  permissions: ToolPermission[],
): AgentToolName[] => {
  if (!permissions.includes('web_access')) {
    return tools;
  }
  if (!tools.includes('web_search') && !tools.includes('web_fetch')) {
    return tools;
  }
  return tools.includes('delegate_research') ? tools : [...tools, 'delegate_research'];
};

const toAgentConfig = (value: unknown): AgentConfig | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const permissions = normalizePermissionList(candidate.permissions);
  return {
    enabled: Boolean(candidate.enabled),
    llmProvider: candidate.llmProvider === 'anthropic' ? 'anthropic' : 'openrouter',
    defaultModel:
      String(candidate.defaultModel ?? DEFAULT_AGENT_CONFIG.defaultModel).trim() ||
      DEFAULT_AGENT_CONFIG.defaultModel,
    permissions,
    maxLoopIterations: Math.max(
      1,
      Math.min(
        20,
        Math.floor(Number(candidate.maxLoopIterations ?? DEFAULT_AGENT_CONFIG.maxLoopIterations)),
      ),
    ),
    maxTokensPerTask: Math.max(
      1_000,
      Math.min(
        100_000,
        Math.floor(Number(candidate.maxTokensPerTask ?? DEFAULT_AGENT_CONFIG.maxTokensPerTask)),
      ),
    ),
    enabledTools: ensureDelegateResearchTool(
      normalizeAgentToolList(candidate.enabledTools),
      permissions,
    ),
    enabledSkills: Array.isArray(candidate.enabledSkills)
      ? candidate.enabledSkills
          .map((entry) => String(entry).trim())
          .filter((entry) => entry.length > 0)
          .slice(0, 50)
      : [],
  };
};

const toAgentConfigWritePayload = (
  value: AgentConfig | null | undefined,
): Record<string, unknown> | null => {
  if (!value) {
    return null;
  }

  return {
    enabled: value.enabled,
    llmProvider: value.llmProvider === 'anthropic' ? 'anthropic' : 'openrouter',
    defaultModel: value.defaultModel.trim() || DEFAULT_AGENT_CONFIG.defaultModel,
    permissions: normalizePermissionList(value.permissions),
    maxLoopIterations: Math.max(1, Math.min(20, Math.floor(value.maxLoopIterations))),
    maxTokensPerTask: Math.max(1_000, Math.min(100_000, Math.floor(value.maxTokensPerTask))),
    enabledTools: ensureDelegateResearchTool(
      normalizeAgentToolList(value.enabledTools),
      normalizePermissionList(value.permissions),
    ),
    enabledSkills: Array.isArray(value.enabledSkills)
      ? value.enabledSkills
          .map((entry) => entry.trim())
          .filter((entry) => entry.length > 0)
          .slice(0, 50)
      : [],
  };
};

export const toNpcDefinition = (id: string, data: Record<string, unknown>): NpcDefinition => {
  const moodProfile =
    data.moodProfile && typeof data.moodProfile === 'object'
      ? normalizeMoodProfile(data.moodProfile as Partial<NpcMoodProfile>)
      : undefined;

  return {
    id,
    name: String(data.name ?? 'Unknown'),
    title: String(data.title ?? ''),
    expertise: String(data.expertise ?? ''),
    personality: String(data.personality ?? ''),
    backstory: String(data.backstory ?? ''),
    greeting: String(data.greeting ?? ''),
    spriteId: String(data.spriteId ?? 'npc_01'),
    avatarLayers: toAvatarLayerSelection(data.avatarLayers),
    spriteColors:
      data.spriteColors && typeof data.spriteColors === 'object'
        ? {
            hair: toHexColor(
              (data.spriteColors as Record<string, unknown>).hair,
              DEFAULT_SPRITE_COLORS.hair,
            ),
            clothing: toHexColor(
              (data.spriteColors as Record<string, unknown>).clothing,
              DEFAULT_SPRITE_COLORS.clothing,
            ),
          }
        : undefined,
    avatarMode: toAvatarMode(data.avatarMode),
    customAvatarId: toOptionalString(data.customAvatarId),
    personalitySpec: toPersonalitySpec(data.personalitySpec),
    llmProvider: (data.llmProvider === 'openrouter' || data.llmProvider === 'anthropic'
      ? data.llmProvider
      : 'gemini') as NpcDefinition['llmProvider'],
    llmModel: canonicalizeProviderModel(
      (data.llmProvider === 'openrouter' || data.llmProvider === 'anthropic'
        ? data.llmProvider
        : 'gemini') as NpcDefinition['llmProvider'],
      String(data.llmModel ?? DEFAULT_LLM_MODEL),
    ),
    fallbackProviders: normalizeFallbackProviders(data.fallbackProviders),
    temperature: Number(data.temperature ?? DEFAULT_NPC_TEMPERATURE),
    maxTokens: Number(data.maxTokens ?? DEFAULT_NPC_MAX_TOKENS),
    source: data.source === 'user' || data.source === 'inline' ? data.source : 'system',
    sourceNpcId: data.sourceNpcId ? String(data.sourceNpcId).trim() : undefined,
    ownerId: String(data.ownerId ?? ''),
    visibility: data.visibility === 'public' ? 'public' : 'private',
    isActive: Boolean(data.isActive),
    agentConfig: toAgentConfig(data.agentConfig),
    ...(moodProfile ? { moodProfile } : {}),
    ...(data.moodState && typeof data.moodState === 'object'
      ? {
          moodState: normalizeMoodState(
            data.moodState as Parameters<typeof normalizeMoodState>[0],
            moodProfile,
          ),
        }
      : {}),
    createdAt: toTimestampDate(data.createdAt),
    updatedAt: toTimestampDate(data.updatedAt),
  };
};

export const toUserNpcConfig = (id: string, data: Record<string, unknown>): UserNpcConfig => {
  const baseNpc = toNpcDefinition(id, data);
  return {
    ...baseNpc,
    source: 'user',
    title: baseNpc.title ?? '',
    expertise: baseNpc.expertise ?? '',
    avatarLayers: baseNpc.avatarLayers ?? DEFAULT_AVATAR_LAYER_SELECTION,
    spriteColors: baseNpc.spriteColors ?? DEFAULT_SPRITE_COLORS,
    avatarMode: baseNpc.avatarMode ?? 'layers',
    customAvatarId: baseNpc.customAvatarId ?? null,
    personalitySpec: baseNpc.personalitySpec ?? null,
    ownerId: baseNpc.ownerId ?? '',
    visibility: baseNpc.visibility ?? 'private',
    isActive: baseNpc.isActive ?? false,
    agentConfig: baseNpc.agentConfig ?? null,
    moodProfile: baseNpc.moodProfile ?? normalizeMoodProfile(null),
  };
};

export const toUserNpcWritePayload = (
  ownerId: string,
  payload: UserNpcUpsertInput,
): Record<string, unknown> => {
  const name = payload.name.trim();
  if (!name) {
    throw new Error('NPC name cannot be empty.');
  }
  return {
    name,
    title: payload.title.trim(),
    expertise: payload.expertise.trim(),
    personality: payload.personality.trim(),
    backstory: payload.backstory.trim(),
    greeting: payload.greeting.trim(),
    avatarLayers: normalizeAvatarLayerSelection(payload.avatarLayers),
    spriteColors: {
      hair: toHexColor(payload.spriteColors.hair, DEFAULT_SPRITE_COLORS.hair),
      clothing: toHexColor(payload.spriteColors.clothing, DEFAULT_SPRITE_COLORS.clothing),
    },
    avatarMode: payload.avatarMode === 'custom' ? 'custom' : 'layers',
    customAvatarId: payload.customAvatarId?.trim() || null,
    personalitySpec: payload.personalitySpec,
    spriteId: 'npc_user',
    llmProvider: payload.llmProvider,
    llmModel: canonicalizeProviderModel(payload.llmProvider, payload.llmModel),
    fallbackProviders: cloneFallbackProviders(payload.fallbackProviders),
    temperature: Math.max(0, Math.min(1, payload.temperature)),
    maxTokens: Math.max(32, Math.min(16384, Math.floor(payload.maxTokens))),
    ownerId,
    visibility: payload.visibility,
    isActive: payload.isActive,
    agentConfig: toAgentConfigWritePayload(payload.agentConfig),
    moodProfile: normalizeMoodProfile(payload.moodProfile),
    updatedAt: serverTimestamp(),
  };
};

const MAX_NPC_NAME_LENGTH = 30;

export const duplicateNpcName = (name: string): string => {
  const base = `${name.trim()} Copy`;
  return base.length > MAX_NPC_NAME_LENGTH ? base.slice(0, MAX_NPC_NAME_LENGTH) : base;
};

export const toDefaultUserNpcInput = (): UserNpcUpsertInput => ({
  name: 'New NPC',
  title: 'Guide',
  expertise: '',
  backstory: '',
  personality: '',
  greeting: 'Hello there.',
  avatarLayers: DEFAULT_AVATAR_LAYER_SELECTION,
  spriteColors: DEFAULT_SPRITE_COLORS,
  avatarMode: 'layers',
  customAvatarId: null,
  personalitySpec: {
    ...DEFAULT_PERSONALITY_SPEC,
    sliders: { ...DEFAULT_PERSONALITY_SPEC.sliders },
    output: { ...DEFAULT_PERSONALITY_SPEC.output },
    mbti: null,
    enneagram: null,
  },
  llmProvider: DEFAULT_LLM_PROVIDER,
  llmModel: DEFAULT_LLM_MODEL,
  fallbackProviders: [],
  temperature: DEFAULT_NPC_TEMPERATURE,
  maxTokens: DEFAULT_NPC_MAX_TOKENS,
  visibility: 'private',
  isActive: false,
  moodProfile: normalizeMoodProfile(null),
  agentConfig: {
    ...DEFAULT_AGENT_CONFIG,
    permissions: [...DEFAULT_AGENT_CONFIG.permissions],
    enabledTools: [...DEFAULT_AGENT_CONFIG.enabledTools],
    enabledSkills: [...DEFAULT_AGENT_CONFIG.enabledSkills],
  },
});
